<script>
    import Alert from "@itslearning/prometheus/assets/modals/Alert/v1/Alert.svelte";
    import AvatarCell from '@itslearning/prometheus/assets/lists/AvatarCell/v1/AvatarCell.svelte';
    import Banner from '@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte';
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import Cell from '@itslearning/prometheus/assets/lists/Cell/v1/Cell.svelte';
    import CheckboxCell from '@itslearning/prometheus/assets/lists/CheckboxCell/v1/CheckboxCell.svelte';
    import EmptyState from '@itslearning/prometheus/assets/nodes/EmptyState/v1/EmptyState.svelte';
    import IconButton from '@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte';  
    import Link from "@itslearning/prometheus/assets/Navigation/Link/v1/Link.svelte";
    import List from '@itslearning/prometheus/assets/lists/List/v1/List.svelte';
    import ListElement from '@itslearning/prometheus/assets/lists/ListElement/v1/ListElement.svelte';
    import LoadingSpinner from '@itslearning/prometheus/assets/nodes/LoadingSpinner/v1/LoadingSpinner.svelte';
    import Modal from '@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte';
    import Switch from '@itslearning/prometheus/assets/inputs/Switch/v1/Switch.svelte';
    import TextCell from '@itslearning/prometheus/assets/lists/TextCell/v1/TextCell.svelte';
    import SearchField from "@itslearning/prometheus/assets/inputs/SearchInput/v1/SearchInput.svelte";
    import RelativeDate from '../RelativeDate/RelativeDate.svelte';
    import {txt} from '../../i18n/i18n';
    import {onMount} from 'svelte';
    import {navigateToViewAnswerPage} from '../../navigation';
    import {survey, use12HTimeFormat, locale, olsonTimeZoneId, i18nRelativeDate, respondentsPageState, navigation, resetSurveyAnswer, deadline} from '../../stores.js';
    import {getRespondents, deleteRespondents} from '../../api';

    let respondents = undefined;
    let isAutoloadingOn = true;
    let errorFromApi = false;
    let warningText = txt('Unknown_error');
    let headerChecked = false;
    let respondentsDictionary = {};
    let scrollableDiv;
    let currentPageIndex = 0;
    let numberOfPages;
    let intervalId;
    let searchText = $respondentsPageState.search;
    let searchTimeoutId = -1;
    let totalNumberOfRespondents = 0;
    let alertVisible = false;
    let confirmVisible = false;
    let isDeleting = false;
    let isLoadingData = false;
    let isBackgoundWorkerWorking = false; 

    let alertI18n = {
        CONFIRM: txt('Ok')
    };

    $: if ($navigation.viewTabId === 'tabResults') {
        getData();
    }

    onMount(async () => {
        intervalId = setIntervalRefresh()
    });
    
    function setIntervalRefresh() {
        if(intervalId){
            clearInterval(intervalId);
        }
        // Refresh list every 2 minutes.
        intervalId = setInterval(async ()=> {
            isBackgoundWorkerWorking= true;
            await getData();
            isBackgoundWorkerWorking = false;
        }, 120000);
        return intervalId;
    }

    function onHeaderCheckedChanged({ checked }) {
        respondents.forEach((item) => {
            item.checked = checked;
        });
        respondents = respondents;
    }

    async function deleteResponses(){
        if(!respondents.some((x) => x.checked)) {
            alertVisible = true;
            return;
        }
        confirmVisible = true;
    }
    
    async function performDeleteResponses(){
        confirmVisible = false;
        let deleteIds = respondents.filter(x => x.checked).map(x => x.answerId);
        if (deleteIds.length === 0) {
            return;
        }

        try {
            isDeleting = true;
            await deleteRespondents(deleteIds);

            for(let i = respondents.length - 1; i >= 0; i--) {
                let respondent = respondents[i];
                if (respondent.checked){
                    respondents.splice(i,1);
                    delete respondentsDictionary[respondent.answerId];
                }
            }
                
            if(deleteIds.includes($survey.answerId)){
                // Clean up own answer if deleted
                resetSurveyAnswer();
            }
            respondents = respondents;
            totalNumberOfRespondents = totalNumberOfRespondents - deleteIds.length;
        } catch {
            errorFromApi = true;
        }
        isDeleting = false;
    }

    async function getData(pageIndex = 0){
        if ($navigation.viewTabId !== 'tabResults') {
            return;
        }

        try {
            isLoadingData = true;
            setIntervalRefresh(); // Reset refresh interval
            const result = await getRespondents(searchText, pageIndex);
            const items = result.respondents;
            currentPageIndex = result.metadata.currentPageIndex;
            numberOfPages = result.metadata.totalPages;
            totalNumberOfRespondents = result.metadata.totalCount;

            items.forEach(respondent => {
                if (respondentsDictionary[respondent.answerId] === undefined) {
                    respondent.checked = false;
                    respondentsDictionary[respondent.answerId] = respondent;
                } else {
                    respondent.checked = respondentsDictionary[respondent.answerId].checked;
                    respondentsDictionary[respondent.answerId] = respondent;
                }
            });
            respondents = Object.values(respondentsDictionary)
            respondents.sort(compareRespondentsByDate)
            filterRespondents();
            respondents = respondents;
            errorFromApi = false;
        } catch {
            errorFromApi = true;
        }
        isLoadingData = false;
    }

    function filterRespondents() {
        if(searchText.length === 0) {
            return;
        }
        for(let i = respondents.length - 1; i >= 0; i--) {
            let names =  respondents[i].name.split(' ');
            let match = false;
            for(let j= 0; j< names.length;j++){
                if(names[j].length > 0 && names[j].toLowerCase().indexOf(searchText.toLocaleLowerCase()) === 0){
                    match = true;
                    break;
                }
            }
            if(!match) {
                respondents.splice(i,1);
            }
        }
    }
          
    async function onScroll() {
        
        if (scrollableDiv.scrollTop > scrollableDiv.scrollHeight - (scrollableDiv.offsetHeight+20)) {
            if (numberOfPages > (currentPageIndex + 1) && !isLoadingData && isAutoloadingOn) {
                await showMore();
            }
        }
    }

    async function showMore() {
        let index = respondents.length;
        await getData(currentPageIndex+1)
        if (respondents.length >= index && !isAutoloadingOn) {
            changeFocus(index);
        }
    }

    async function search() {
        $respondentsPageState.search = searchText;
        clearTimeout(searchTimeoutId);
        searchTimeoutId = setTimeout(() =>{getData();}, 400); // Wait 400ms before doing the search. Wait until user is done typing.
    }
    
    function changeFocus(index)
    {
        let answerId = respondents[index].answerId;
        let elementId = "a[href='" + `#show-result-${answerId}` + "']";
        if(document.querySelector(elementId) !== null)
        {
            document.querySelector(elementId).focus();
        }
    }

    function compareRespondentsByDate(a, b) {
        let aAnswered = isValidDate(a.answered);
        let bAnswered = isValidDate(b.answered);

        if(aAnswered && !bAnswered) {
            return 1;
        }

        if(bAnswered && !aAnswered) {
            return -1;
        }

        let aDate = aAnswered ? a.answered : a.started;
        let bDate = bAnswered ? b.answered : b.started;
        
        if(aDate > bDate){
            return -1;
        }
        else if(bDate > aDate){
            return 1;
        }
        return 0;
    }

    function toggleAutoloading() {
        isAutoloadingOn = !isAutoloadingOn;
    }
   
    function hasDeadline() {
        return isValidDate(deadline);
    }

    function isValidDate(date) {
        let dateValue = new Date(date);
        return dateValue > new Date('0001-01-01T00:00:00Z');
    }
  
</script>
{#if !respondents}
    <div class="spinner">
        <LoadingSpinner size="large" visible={true}/>
    </div>
{:else}
    <Banner type="warning" text={warningText} close={txt('Close')} visible={errorFromApi} on:hidden={() => errorFromApi = false} />
    {#if respondents.length === 0}
        <EmptyState title={txt('Empty_state_no_responses_heading')} description={txt('Empty_state_no_responses_text')} iconName="test-results" />
    {:else}
        <div class="toolbar">
            <Alert heading={txt('Select_a_response_to_delete')} i18n={alertI18n} bind:enabled={alertVisible}></Alert>
            <Modal enabled={confirmVisible} 
                titleText={txt('Delete_responses')}
                titleOnly={true}
                cancelText={txt('Cancel')}
                confirmText={txt('Delete')}
                on:confirm={performDeleteResponses}
                on:cancel={() => {confirmVisible = false;}}
                on:close={() => {confirmVisible = false;}}
            />
            <div class="left">
                <IconButton htmlType="button" iconClass="trash-button-icon" text={txt('Delete')} type="secondary" on:click={deleteResponses} loading={isDeleting} />
            </div>
            <div class="spinner">
                <LoadingSpinner visible={isLoadingData && !isBackgoundWorkerWorking} size="large" />
            </div>
            <div class="right">
                {#if $survey.respondentType === 1}
                    <div class="search-box">
                        <SearchField
                            maxlength="20" 
                            label={txt('Search')} 
                            labelHidden=true on:keyup={search} on:clear={search}
                            bind:value={searchText} 
                            height="small" 
                            placeholder={txt('Search_for_respondents')} />
                    </div>
                {/if}
            </div>
        </div>
        <div class="list-container" bind:this={scrollableDiv} on:scroll={onScroll}>
            {#if numberOfPages > currentPageIndex + 1}
                <Switch name="its-plans__autoloading-toggle" label={txt('Autoloading')} bind:checked={isAutoloadingOn} on:click={toggleAutoloading}/>
            {/if}
            <List>
                <thead slot="header">
                    <ListElement header>
                        <CheckboxCell bind:checked={headerChecked} text="Check all" header on:change={(e) => onHeaderCheckedChanged(e.detail)} />
                        <AvatarCell text={txt('Avatar')} header/>
                        <TextCell text={txt('Respondent')} header />
                        {#if respondents.length > 0 && respondents[0].extraUserInfoTitle}
                            <TextCell text="{respondents[0].extraUserInfoTitle}" header />
                        {/if}
                        <TextCell text={txt('Submitted')} header priority="2"/>
                        <TextCell text={txt('Note')} header priority="3"/>
                    </ListElement>
                </thead>
            
                <tbody slot="body">
                    {#if respondents.length === 0}
                        <TextCell text={txt('No_respondents')} colspan={5} />
                    {:else}
                        {#each respondents as respondent (respondent.answerId)}
                            <ListElement bind:checked={respondent.checked} checkOnClick>
                                <CheckboxCell bind:checked={respondent.checked} text="Checked" />
                                <AvatarCell src={respondent.profileImageSmallUrl} />
                                {#if isValidDate(respondent.answered)}
                                    <Cell type="title">
                                        <Link href="#show-result-{respondent.answerId}" slot="data" on:click={() => navigateToViewAnswerPage(respondent.answerId)} text="{respondent.name}" />
                                    </Cell>
                                {:else}
                                    <Cell type="title">
                                        <p slot="data">{respondent.name}</p>
                                    </Cell>
                                {/if}
                                {#if respondent.extraUserInfoTitle}
                                    <TextCell text={respondent.extraUserInfo} />
                                {/if}
                                {#if isValidDate(respondent.answered)}
                                    <Cell type="text" priority="2">
                                        <RelativeDate date={new Date(respondent.answered)} 
                                                    twelveHourFormat={use12HTimeFormat} 
                                                    timeZone={olsonTimeZoneId} 
                                                    locale={locale} 
                                                    i18n={i18nRelativeDate}
                                                    slot="data" />
                                    </Cell>
                                    <TextCell text={hasDeadline() && new Date(respondent.answered) > deadline ? txt('Submitted_after_deadline') : ''}  priority="3"/>
                                {:else} 
                                    <Cell type="text" priority="2">
                                        <p slot="data" >-</p>
                                    </Cell>
                                    <Cell type="text" priority="3">
                                        <span slot="data">
                                            <span>{txt('Started')}: </span>
                                            <RelativeDate date={new Date(respondent.started)} 
                                                        twelveHourFormat={use12HTimeFormat} 
                                                        timeZone={olsonTimeZoneId} 
                                                        locale={locale} 
                                                        i18n={i18nRelativeDate}
                                                        />
                                        </span>
                                    </Cell>
                                {/if}
                            </ListElement>
                        {/each}
                    {/if}
                </tbody>
            </List>
            {#if !isAutoloadingOn && totalNumberOfRespondents > Object.values(respondentsDictionary).length}
                <div class="show-more">
                    {#if errorFromApi}
                        <span class="show-more-error">{txt('Unknown_error')}</span>
                    {/if}
                    <Button type="plain" htmlType="button" text={txt('Show_more')} on:click={showMore} />
                </div>
            {/if}
        </div>
        <span class="table-footer" aria-live="polite">{txt('Showing_x_responses_of_total_y_responses', [respondents.length ,totalNumberOfRespondents ])}</span>
    {/if}
{/if}
